import { LitElement, html, css } from 'lit';

import { openError } from '../../../../neb-dialog/neb-banner-state';
import { store } from '../../../../neb-redux/neb-redux-store';
import {
  CSS_COLOR_WHITE,
  CSS_SPACING,
  CSS_COLOR_HIGHLIGHT,
  CSS_FONT_WEIGHT_BOLD,
  CSS_COLOR_BLACK,
  CSS_COLOR_GREY_1,
} from '../../../../neb-styles/neb-variables';
import { BUTTON_ROLE } from '../neb-button';

import './neb-two-factor-auth';
import '../../../../../src/components/misc/neb-icon';
import '../../../../../src/components/controls/inputs/neb-checkbox';

export const ELEMENTS = {
  twoFactorAuth: { id: 'neb-2fa-auth-page-two-factor-auth' },
  submitButton: { id: 'neb-2fa-auth-page-submit-button' },
  cancelButton: { id: 'neb-2fa-auth-page-cancel-button' },
  rememberDeviceCheckbox: { id: 'neb-2fa-auth-page-remember-device-checkbox' },
};

export const AUTH_PAGE_TYPES = {
  TOTP: 'totp',
  SMS: 'sms',
};

class Neb2faAuthPage extends LitElement {
  static get properties() {
    return {
      error: { type: String },
      authType: { type: String },
      challengeParameters: { type: Object },
      rememberDevice: { type: Boolean },
      __isSubmitting: { type: Boolean },
      __code: { type: String },
      __hasCompleteCode: { type: Boolean },
      __focused: { type: Boolean },
    };
  }

  constructor() {
    super();
    this.__initState();
    this.__initHandlers();
  }

  __initState() {
    this.error = '';
    this.authType = AUTH_PAGE_TYPES.TOTP;
    this.challengeParameters = null;
    this.rememberDevice = false;
    this.__isSubmitting = false;
    this.__code = '';
    this.__hasCompleteCode = false;
    this.__focused = false;

    this.onSubmit = () => {};

    this.onCancel = () => {};
  }

  __initHandlers() {
    this.__handlers = {
      updateCode: (code, isValid) => {
        this.__code = code;
        this.__hasCompleteCode = isValid;
        this.requestUpdate();

        if (this.autoSubmitOnComplete && isValid && !this.__isSubmitting) {
          this.__isSubmitting = true;
          this.onSubmit(code, true);
        }
      },
      submit: () => {
        const verifyBtn = this.shadowRoot.getElementById(
          ELEMENTS.submitButton.id,
        );
        const verifyDisabled = verifyBtn && verifyBtn.hasAttribute('disabled');

        if (verifyDisabled || !this.__hasCompleteCode || this.__isSubmitting) {
          return;
        }

        this.__isSubmitting = true;
        this.onSubmit(this.__code, this.rememberDevice);
      },
      cancel: () => {
        this.onCancel();
      },
      rememberDeviceChange: e => {
        this.rememberDevice = e.value;
      },
      focusRememberDevice: e => {
        if (e) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
        const checkbox = this.shadowRoot.getElementById(
          ELEMENTS.rememberDeviceCheckbox.id,
        );

        if (checkbox) {
          setTimeout(() => {
            checkbox.focus();
          }, 0);
        }
      },
      focusVerifyButton: e => {
        const verifyBtn = this.shadowRoot.getElementById(
          ELEMENTS.submitButton.id,
        );
        const verifyDisabled = verifyBtn && verifyBtn.hasAttribute('disabled');

        if (e.key === 'Tab' && e.shiftKey) {
          if (!verifyDisabled) {
            e.preventDefault();
            setTimeout(() => {
              verifyBtn.focus();
            }, 0);
          }
        }
      },
      focusCancelButton: e => {
        if (e.key === 'Tab' && e.shiftKey) {
          e.preventDefault();
          const verifyBtn = this.shadowRoot.getElementById(
            ELEMENTS.submitButton.id,
          );
          const isVerifyDisabled =
            verifyBtn && verifyBtn.hasAttribute('disabled');

          if (verifyBtn && !isVerifyDisabled) {
            verifyBtn.focus();
          } else {
            const checkbox = this.shadowRoot.getElementById(
              ELEMENTS.rememberDeviceCheckbox.id,
            );

            if (checkbox) {
              checkbox.focus();
            }
          }
        }
      },
      focus: () => {
        this.__focused = true;
      },
      blur: () => {
        this.__focused = false;
      },
      submitButtonKeydown: e => {
        const verifyDisabled = !this.__hasCompleteCode || this.__isSubmitting;

        if (e.key === 'Enter' && !verifyDisabled) {
          e.preventDefault();
          this.__handlers.submit();
        } else if (e.key === 'Tab' && e.shiftKey) {
          e.preventDefault();
          this.__focusCheckbox();
        }
      },
      rememberDeviceCheckboxKeydown: e => {
        if (e.key === 'Tab' && e.shiftKey) {
          e.preventDefault();
          const twoFactorAuth = this.shadowRoot.getElementById(
            ELEMENTS.twoFactorAuth.id,
          );

          if (twoFactorAuth) {
            const inputs =
              twoFactorAuth.shadowRoot.querySelectorAll('.code-input');

            if (inputs && inputs.length) {
              inputs[inputs.length - 1].focus();
            }
          }
        } else {
          this.__handlers.focusVerifyButton(e);
        }
      },
      cancelButtonKeydown: e => {
        if (e.key === 'Tab' && e.shiftKey) {
          e.preventDefault();

          if (!this.__focusVerifyButtonIfEnabled()) {
            this.__focusCheckbox();
          }
        }
      },
    };
  }

  __clearCodeInput() {
    const twoFactorAuth = this.shadowRoot.getElementById(
      ELEMENTS.twoFactorAuth.id,
    );

    if (twoFactorAuth) {
      setTimeout(() => {
        this.__isSubmitting = false;
        twoFactorAuth.clearCode();
      }, 1000);
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('error') && this.error) {
      store.dispatch(openError(this.error));
      this.__clearCodeInput();
    }
  }

  static get styles() {
    return [
      css`
        .two-factor-auth-container {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          background-color: ${CSS_COLOR_WHITE};
          border-radius: 5px;
          width: fit-content;
        }

        .title {
          font-size: 24px;
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
          margin-bottom: ${CSS_SPACING};
          color: ${CSS_COLOR_BLACK};
          text-align: left;
          width: 100%;
          margin-left: 0;
        }

        .subtitle {
          font-size: 16px;
          color: ${CSS_COLOR_GREY_1};
          text-align: left;
          margin-bottom: 20px;
          width: 100%;
          margin-left: 0;
        }

        .enter-code-label {
          font-size: 18px;
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
          color: ${CSS_COLOR_BLACK};
          margin-top: 8px;
          margin-bottom: 8px;
          text-align: left;
          margin-left: 30px;
        }

        .code-input-row {
          margin-left: 42px;
        }

        .remember-device-container {
          margin-top: 20px;
          margin-left: 30px;
        }

        .buttons-container {
          display: flex;
          justify-content: space-between;
          width: 100%;
          margin-top: 32px;
        }

        .authenticator-icon {
          width: 60px;
          height: 60px;
          margin-bottom: 15px;
          fill: ${CSS_COLOR_HIGHLIGHT};
        }

        neb-button::part(button),
        neb-button::part(button):focus,
        neb-button::part(button):focus-visible {
          outline: none !important;
          outline-offset: 0 !important;
        }

        neb-button::part(button):focus-visible {
          box-shadow:
            0 0 0 2px white,
            0 0 0 4px ${CSS_COLOR_HIGHLIGHT};
          border-radius: 5px;
        }

        .remember-device-container neb-checkbox,
        .remember-device-container neb-checkbox:focus,
        .remember-device-container neb-checkbox:focus-visible {
          outline: none !important;
          outline-offset: 0 !important;
        }

        .remember-device-container neb-checkbox:focus-visible {
          box-shadow:
            0 0 0 2px white,
            0 0 0 4px ${CSS_COLOR_HIGHLIGHT};
          border-radius: 4px;
        }

        #neb-2fa-auth-page-cancel-button:focus-visible::part(outline),
        #neb-2fa-auth-page-cancel-button:focus::part(outline) {
          border: none !important;
        }

        #neb-2fa-auth-page-cancel-button::part(outline)[focused] {
          border: none !important;
        }
      `,
    ];
  }

  __computePhoneText() {
    let phoneText = 'Check your phone for a 6-digit verification code.';

    try {
      const phoneInfo = this.challengeParameters?.CODE_DELIVERY_DESTINATION;

      if (phoneInfo && phoneInfo.length >= 4) {
        const lastFour = phoneInfo.slice(-4);
        phoneText = `We've sent a code to a phone number ending in ${lastFour}.`;
      }
    } catch (e) {
      console.error(e);
    }

    return html`
      <div>${phoneText}</div>
      <div>You should receive a text message with the code shortly.</div>
    `;
  }

  __focusCheckbox() {
    const checkbox = this.shadowRoot.getElementById(
      ELEMENTS.rememberDeviceCheckbox.id,
    );
    if (checkbox) checkbox.focus();
  }

  __focusVerifyButtonIfEnabled() {
    const verifyBtn = this.shadowRoot.getElementById(ELEMENTS.submitButton.id);
    const isVerifyDisabled = verifyBtn && verifyBtn.hasAttribute('disabled');

    if (verifyBtn && !isVerifyDisabled) {
      verifyBtn.focus();
      return true;
    }
    return false;
  }

  render() {
    const subtitleText =
      this.authType === AUTH_PAGE_TYPES.TOTP
        ? 'Check your Authenticator app for 6-digit code.'
        : this.__computePhoneText();
    const isAuthenticator = this.authType === AUTH_PAGE_TYPES.TOTP;
    const verifyDisabled = !this.__hasCompleteCode || this.__isSubmitting;

    return html`
      <div class="two-factor-auth-container">
        <div class="title">2-Step Verification</div>
        <div class="subtitle">${subtitleText}</div>
        <div class="enter-code-label">Enter Code</div>
        <div class="code-input-row">
          <neb-two-factor-auth
            id="${ELEMENTS.twoFactorAuth.id}"
            ?authenticator="${isAuthenticator}"
            ?strictValidation="${true}"
            .onChange="${this.__handlers.updateCode}"
            .autoSubmitOnComplete="${false}"
            @enter-pressed="${() => {
              if (!this.__isSubmitting && this.__hasCompleteCode) {
                this.__handlers.submit();
              }
            }}"
            @tabbed-past-last-input="${this.__handlers.focusRememberDevice}"
          ></neb-two-factor-auth>
        </div>

        <div class="remember-device-container">
          <neb-checkbox
            id="${ELEMENTS.rememberDeviceCheckbox.id}"
            label="Remember this device"
            .checked="${this.rememberDevice}"
            .onChange="${this.__handlers.rememberDeviceChange}"
            ?disabled="${this.__isSubmitting}"
            @keydown="${this.__handlers.rememberDeviceCheckboxKeydown}"
          ></neb-checkbox>
        </div>

        <div class="buttons-container">
          <neb-button
            id="${ELEMENTS.submitButton.id}"
            class="button"
            .label="${this.__isSubmitting ? 'Verifying' : 'Verify'}"
            .role="${BUTTON_ROLE.CONFIRM}"
            .onClick="${this.__handlers.submit}"
            ?disabled="${verifyDisabled}"
            @keydown="${this.__handlers.submitButtonKeydown}"
          ></neb-button>

          <neb-button
            id="${ELEMENTS.cancelButton.id}"
            label="Cancel"
            .role="${BUTTON_ROLE.OUTLINE}"
            .onClick="${this.__handlers.cancel}"
            @keydown="${this.__handlers.cancelButtonKeydown}"
          ></neb-button>
        </div>
      </div>
    `;
  }
}

customElements.define('neb-2fa-auth-page', Neb2faAuthPage);
